import { Conditions, Fields } from '@root/constants'
import { isVATObliged, useBusinessContext } from '@root/context'
import {
  fetchAccount,
  fetchContact,
  IAccountingRule,
  IAccountingRuleBlueprintEntry,
  Rule
} from '@root/query'
import { formatCurrency } from '@root/utils'
import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'
import { Control, FieldErrors, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { getAmountRangeFromRules } from './AmountFilter'
import { accrualDateLabelKey, isPurchaseInvoiceSource } from './accrualDateMode'

export type SectionId = 1 | 2 | 3 | 4

export type SectionOpen = Record<SectionId, boolean>

const ACCOUNT_CACHE_MS = 15 * 60 * 1000

type AccountCache = {
  type: 'account'
  item: { id: number; number: string; name: string }
  id: number
}

type SectionErrorFlags = {
  paymentAccount?: boolean
  ruleNode?: boolean
  booking?: boolean
}

const hasText = (value: unknown) => value != null && String(value).trim() !== ''

export const initialSectionOpen = (isPrefilled: boolean): SectionOpen => ({
  1: true,
  2: true,
  3: isPrefilled,
  4: isPrefilled
})

export const sectionErrorFlags = (
  errors: FieldErrors<Partial<IAccountingRule>>
): SectionErrorFlags => ({
  paymentAccount: Boolean(errors.payment_account),
  ruleNode: Boolean(errors.rule_node),
  booking: Boolean(errors.description || errors.blueprint || errors.accrual_date_mode)
})

export const openSectionsWithErrors = (
  current: SectionOpen,
  flags: SectionErrorFlags
): SectionOpen => ({
  1: current[1] || Boolean(flags.paymentAccount),
  2: current[2] || Boolean(flags.ruleNode),
  3: current[3] || Boolean(flags.booking),
  4: current[4]
})

const FIELD_ERROR_KEYS = new Set(['type', 'message', 'ref', 'types'])

const walkError = (error: unknown, prefix: string): string | undefined => {
  if (!error || typeof error !== 'object') return undefined

  if (Array.isArray(error)) {
    for (let index = 0; index < error.length; index++) {
      const nested = walkError(error[index], `${prefix}.${index}`)
      if (nested) return nested
    }
    return undefined
  }

  const record = error as Record<string, unknown>
  const keys = Object.keys(record)
  const isFieldError = keys.length > 0 && keys.every(key => FIELD_ERROR_KEYS.has(key))
  if (isFieldError) return prefix

  for (const key of keys) {
    if (FIELD_ERROR_KEYS.has(key)) continue
    const nested = walkError(record[key], `${prefix}.${key}`)
    if (nested) return nested
  }

  return 'type' in record ? prefix : undefined
}

export const firstErrorPath = (
  errors: FieldErrors<Partial<IAccountingRule>>
): string | undefined => {
  const preferred = [
    'name',
    'payment_account',
    'accrual_date_mode',
    'rule_node',
    'description',
    'blueprint',
    'tag_ids'
  ]
  const keys = [
    ...preferred.filter(key => key in errors),
    ...Object.keys(errors).filter(key => !preferred.includes(key))
  ]

  for (const key of keys) {
    const path = walkError(errors[key as keyof typeof errors], key)
    if (path) return path
  }

  return undefined
}

const isRule = (rule: Rule | { operand?: string }): rule is Rule => 'field' in rule

const contactIdsFromRules = (rules: Array<Rule | { operand?: string }> | undefined): number[] => {
  const ids = new Set<number>()
  for (const rule of rules ?? []) {
    if (!isRule(rule) || rule.field !== Fields.CONTACT || !Array.isArray(rule.value)) continue
    for (const id of rule.value) {
      if (typeof id === 'number') ids.add(id)
    }
  }
  return [...ids]
}

const accountIdsFromValues = (
  paymentAccount: number | null | undefined,
  entries: Array<IAccountingRuleBlueprintEntry | undefined>
): number[] => {
  const ids = new Set<number>()
  if (typeof paymentAccount === 'number') ids.add(paymentAccount)
  for (const entry of entries) {
    if (typeof entry?.account_id === 'number') ids.add(entry.account_id)
  }
  return [...ids]
}

const readAccountLabel = (data: unknown) => {
  if (!data || typeof data !== 'object') return undefined
  const record = data as {
    number?: string | number
    name?: string
    item?: { number?: string | number; name?: string }
  }
  const source = record.item?.name ? record.item : record
  const name = source.name?.trim()
  if (source.number == null || source.number === '' || !name) return undefined
  return `${source.number} ${name}`
}

const entryIsComplete = (entry: IAccountingRuleBlueprintEntry | undefined, vatObliged: boolean) => {
  if (!hasText(entry?.account_id) || !hasText(entry?.amount)) return false
  if (!vatObliged) return true
  return hasText(entry?.default_vat_code) && hasText(entry?.default_vat_rate_label)
}

const rowsAreComplete = (rows: IAccountingRuleBlueprintEntry[] | undefined, vatObliged: boolean) =>
  Boolean(rows?.length) && rows.every(row => entryIsComplete(row, vatObliged))

export const useAccountingRuleSections = (
  control: Control<Partial<IAccountingRule>>,
  options?: { isWorkflowRule?: boolean }
) => {
  const [t] = useTranslation()
  const { slug, data: business } = useBusinessContext()
  const vatObliged = isVATObliged(business)
  const isWorkflowRule = options?.isWorkflowRule === true

  const paymentAccount = useWatch({ control, name: 'payment_account' })
  const accrualDateMode = useWatch({ control, name: 'accrual_date_mode' })
  const isPurchaseSource = isPurchaseInvoiceSource(
    Boolean(business?.has_accrual_based_entries_for_invoicing),
    accrualDateMode
  )
  const rules = useWatch({ control, name: 'rule_node.rules' })
  const description = useWatch({ control, name: 'description' })
  const blueprint = useWatch({ control, name: 'blueprint' })
  const waitForAttachment = useWatch({ control, name: 'wait_for_attachment' })
  const attachmentExempt = useWatch({ control, name: 'attachment_exempt' })
  const contactName = useWatch({ control, name: 'contact_name' })

  const entries = useMemo(
    () => [...(blueprint?.debet_entries ?? []), ...(blueprint?.credit_entries ?? [])],
    [blueprint?.debet_entries, blueprint?.credit_entries]
  )

  const accountIds = useMemo(
    () => accountIdsFromValues(paymentAccount, entries),
    [paymentAccount, entries]
  )
  const contactIds = useMemo(() => contactIdsFromRules(rules), [rules])

  const accountQueries = useQueries({
    queries: accountIds.map(accountId => ({
      queryKey: [slug, 'accounts', accountId],
      queryFn: async (): Promise<AccountCache> => {
        const account = await fetchAccount({ slug, accountId })
        return {
          type: 'account',
          item: {
            id: account.id,
            number: String(account.number),
            name: account.name
          },
          id: account.id
        }
      },
      staleTime: ACCOUNT_CACHE_MS
    }))
  })

  const contactQueries = useQueries({
    queries: contactIds.map(contactId => ({
      queryKey: [slug, 'contacts', contactId],
      queryFn: () => fetchContact({ slug, contactId }),
      staleTime: 60 * 1000
    }))
  })

  const accountLabels = new Map<number, string>()
  accountIds.forEach((id, index) => {
    const label = readAccountLabel(accountQueries[index]?.data)
    if (label) accountLabels.set(id, label)
  })

  const contactLabels = new Map<number, string>()
  contactIds.forEach((id, index) => {
    const name = contactQueries[index]?.data?.name?.trim()
    if (name) contactLabels.set(id, name)
  })

  const labelFor = (accountId: number | null | undefined) => {
    if (typeof accountId !== 'number') return '–'
    return accountLabels.get(accountId) ?? '…'
  }

  const conditionParts: string[] = []
  for (const rule of rules ?? []) {
    if (!isRule(rule) || rule.field === Fields.MONEY) continue

    if (
      rule.field === Fields.DESCRIPTION ||
      rule.field === Fields.REFERENCE ||
      rule.field === Fields.IBAN
    ) {
      const value = typeof rule.value === 'string' ? rule.value.trim() : ''
      if (!value) continue
      const key =
        rule.field === Fields.DESCRIPTION
          ? 'summaryDescriptionContains'
          : rule.field === Fields.REFERENCE
          ? 'summaryReferenceContains'
          : 'summaryIbanContains'
      conditionParts.push(t(`settings.automation.accountingRules.form.${key}`, { value }))
      continue
    }

    if (rule.field === Fields.CONTACT && rule.condition === Conditions.CONTACT_IS_IN) {
      const ids = Array.isArray(rule.value)
        ? rule.value.filter((id): id is number => typeof id === 'number')
        : []
      const names = ids
        .map(id => contactLabels.get(id))
        .filter((name): name is string => Boolean(name))
      const pending = contactName?.trim()
      if (!ids.length && !pending) continue
      const namesLabel = names.length
        ? names.join(` ${t('settings.automation.accountingRules.form.summaryOr')} `)
        : pending || '…'
      conditionParts.push(
        t('settings.automation.accountingRules.form.summaryContact', { names: namesLabel })
      )
    }
  }

  const amountRange = getAmountRangeFromRules(rules)
  const min = amountRange.min.trim()
  const max = amountRange.max.trim()
  const minNum = min ? Number(min) : null
  const maxNum = max ? Number(max) : null
  if (minNum != null && maxNum != null && !Number.isNaN(minNum) && !Number.isNaN(maxNum)) {
    conditionParts.push(
      minNum === maxNum
        ? t('settings.automation.accountingRules.form.summaryAmountExact', {
            amount: formatCurrency(minNum)
          })
        : t('settings.automation.accountingRules.form.summaryAmountRange', {
            min: formatCurrency(minNum),
            max: formatCurrency(maxNum)
          })
    )
  } else if (minNum != null && !Number.isNaN(minNum)) {
    conditionParts.push(
      t('settings.automation.accountingRules.form.summaryAmountMin', {
        amount: formatCurrency(minNum)
      })
    )
  } else if (maxNum != null && !Number.isNaN(maxNum)) {
    conditionParts.push(
      t('settings.automation.accountingRules.form.summaryAmountMax', {
        amount: formatCurrency(maxNum)
      })
    )
  }

  const joinAccounts = (rows: IAccountingRuleBlueprintEntry[] | undefined) =>
    rows?.map(row => labelFor(row.account_id)).join(', ') || '–'

  const workflowContactSummary =
    contactIds.map(id => contactLabels.get(id)).find((name): name is string => Boolean(name)) ||
    contactName?.trim() ||
    '…'

  const modeLabelKey = accrualDateLabelKey(accrualDateMode)
  const accrualDateLabel = modeLabelKey
    ? t(`settings.automation.accountingRules.form.${modeLabelKey}`).toLocaleLowerCase()
    : undefined
  const bookingSummary = {
    description: description?.trim()
      ? description.trim()
      : t('settings.automation.accountingRules.form.summaryNoDescription'),
    debit: joinAccounts(blueprint?.debet_entries),
    credit: joinAccounts(blueprint?.credit_entries)
  }
  const summaries: Record<SectionId, string> = {
    1: isPurchaseSource
      ? t('settings.automation.accountingRules.form.sourcePurchase')
      : typeof paymentAccount === 'number'
      ? accountLabels.get(paymentAccount) ?? '…'
      : t('settings.automation.accountingRules.form.summaryNoAccount'),
    2: conditionParts.length
      ? conditionParts.join(' · ')
      : t('settings.automation.accountingRules.form.summaryNoConditions'),
    3: isWorkflowRule
      ? t('settings.automation.accountingRules.form.summaryBookingWorkflow', {
          contact: workflowContactSummary,
          debit: joinAccounts(blueprint?.debet_entries),
          credit: joinAccounts(blueprint?.credit_entries)
        })
      : accrualDateLabel
      ? t('settings.automation.accountingRules.form.summaryBookingAccrual', {
          mode: accrualDateLabel,
          ...bookingSummary
        })
      : t('settings.automation.accountingRules.form.summaryBooking', bookingSummary),
    4: t(
      waitForAttachment
        ? 'settings.automation.accountingRules.form.document.waitForAttachment'
        : attachmentExempt
        ? 'document.attachmentExempt'
        : 'settings.automation.accountingRules.form.document.bookWithoutWaiting'
    )
  }

  const complete: Record<SectionId, boolean> = {
    1: isPurchaseSource || typeof paymentAccount === 'number',
    2: conditionParts.length > 0,
    3:
      hasText(description) &&
      rowsAreComplete(blueprint?.debet_entries, vatObliged) &&
      rowsAreComplete(blueprint?.credit_entries, vatObliged),
    4: true
  }

  return { summaries, complete }
}
